/**
 * useReportPages Hook (Story 5.32)
 *
 * Multi-page state management for the report editor.
 * Wraps useUndoStack<ReportPage[]> + separate useState for activePageId.
 *
 * ADR-1: activePageId is NOT in the undo stack (navigation state, not document state).
 * ADR-3: Pages use explicit order field, normalized to contiguous 0-based on every mutation.
 * ADR-5: Undo tracks ReportPage[] only (not version/grid/format).
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useUndoStack } from "./useUndoStack";
import type {
  ReportPage,
  ReportPageWidget,
} from "@provenleap/types/report-rendering";
import {
  MAX_PAGES,
  MAX_WIDGETS_TOTAL,
  MAX_PAGE_TITLE_LENGTH,
  DEFAULT_CATEGORY,
  PAGE_CATEGORY_IDS,
} from "@provenleap/types/report-rendering";

// Re-export for backward compatibility with existing test imports
export { MAX_PAGES, MAX_WIDGETS_TOTAL, MAX_PAGE_TITLE_LENGTH };

// SYNC: packages/db/src/schema/report-layout-types.ts — canonical source
const HTML_INJECTION_PATTERN =
  /<\s*\/?(?:script|iframe|object|embed|form|input|button|link|style|meta|base|svg)\b|on\w+\s*=/i;

// ── Types ─────────────────────────────────────────────────────────────

export interface UseReportPagesReturn {
  // Page state
  pages: ReportPage[];
  activePageId: string;
  activePageWidgets: ReportPageWidget[];

  // Page operations (push to undo stack)
  addPage: (category?: string) => void;
  deletePage: (id: string) => void;
  duplicatePage: (id: string) => void;
  reorderPages: (fromIndex: number, toIndex: number) => void;
  renamePage: (id: string, title: string) => boolean;
  changePageCategory: (id: string, category: string) => void;

  // Custom section (category) label overrides, keyed by category id.
  sectionLabels: Record<string, string>;
  setSectionLabel: (categoryId: string, label: string) => void;

  // Sections that exist independently of pages (so a section can be empty).
  // A category is "shown" if it has pages OR is in this list.
  activeSections: string[];
  addSection: (categoryId: string) => void;
  deleteSection: (categoryId: string) => void;

  // Display order of sections (category ids). Categories not listed fall back
  // to the default PAGE_CATEGORIES order. moveSection reorders within the
  // caller-supplied list of currently-visible section ids (in display order).
  sectionOrder: string[];
  moveSection: (
    categoryId: string,
    direction: "up" | "down",
    visibleIds: string[]
  ) => void;

  // Page navigation (does NOT push to undo stack)
  setActivePageId: (id: string) => void;

  // Widget operations scoped to active page
  updateActivePageWidgets: (
    widgets: ReportPageWidget[] | ((prev: ReportPageWidget[]) => ReportPageWidget[])
  ) => void;

  // Silent widget update (no undo push — for auto-resize)
  updateActivePageWidgetsSilent: (
    widgets: ReportPageWidget[] | ((prev: ReportPageWidget[]) => ReportPageWidget[])
  ) => void;

  // Undo/redo (delegates to useUndoStack)
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // Reset (for template load)
  reset: (
    pages: ReportPage[],
    sectionLabels?: Record<string, string>,
    activeSections?: string[],
    sectionOrder?: string[]
  ) => void;

  // Version counter for dirty detection (increments on every setState)
  version: number;
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useReportPages(
  initialPages: ReportPage[],
  initialSectionLabels: Record<string, string> = {},
  initialActiveSections: string[] = [],
  initialSectionOrder: string[] = []
): UseReportPagesReturn {
  const {
    state: pages,
    setState: setPages,
    setStateSilent: setPagesSilent,
    undo: undoRaw,
    redo: redoRaw,
    reset: resetUndoStack,
    canUndo,
    canRedo,
  } = useUndoStack<ReportPage[]>(initialPages);

  // activePageId is navigation state — NOT in undo stack (ADR-1)
  const [activePageId, setActivePageIdRaw] = useState<string>(
    () => initialPages[0]?.id ?? ""
  );

  // Ref for reading activePageId inside updater functions (avoids stale closures)
  const activePageIdRef = useRef(activePageId);
  activePageIdRef.current = activePageId;

  // Version counter for dirty detection
  const [version, setVersion] = useState(0);

  // Custom section (category) label overrides
  const [sectionLabels, setSectionLabels] = useState<Record<string, string>>(initialSectionLabels);

  const setSectionLabel = useCallback((categoryId: string, label: string) => {
    const trimmed = label.trim().slice(0, 60);
    setSectionLabels((prev) => {
      // Empty resets to the default label (remove the override).
      if (!trimmed) {
        if (!(categoryId in prev)) return prev;
        const next = { ...prev };
        delete next[categoryId];
        setVersion((v) => v + 1);
        return next;
      }
      if (prev[categoryId] === trimmed) return prev; // no-op, no autosave churn
      setVersion((v) => v + 1);
      return { ...prev, [categoryId]: trimmed };
    });
  }, []);

  // Sections that exist independently of pages (empty sections). Not in the
  // undo stack — section structure is sidebar state, version-bumped for dirty
  // detection like sectionLabels.
  const [activeSections, setActiveSections] = useState<string[]>(() =>
    initialActiveSections.filter((id) => PAGE_CATEGORY_IDS.has(id))
  );

  const addSection = useCallback((categoryId: string) => {
    if (!PAGE_CATEGORY_IDS.has(categoryId)) return;
    setActiveSections((prev) => {
      if (prev.includes(categoryId)) return prev; // already present
      setVersion((v) => v + 1);
      return [...prev, categoryId];
    });
  }, []);

  const deleteSection = useCallback((categoryId: string) => {
    setActiveSections((prev) => {
      if (!prev.includes(categoryId)) return prev;
      setVersion((v) => v + 1);
      return prev.filter((id) => id !== categoryId);
    });
  }, []);

  // Display order of visible sections. Empty = use the natural (PAGE_CATEGORIES)
  // order. Populated once the user reorders, holding the full visible list.
  const [sectionOrder, setSectionOrder] = useState<string[]>(() =>
    initialSectionOrder.filter((id) => PAGE_CATEGORY_IDS.has(id))
  );

  const moveSection = useCallback(
    (categoryId: string, direction: "up" | "down", visibleIds: string[]) => {
      const order = [...visibleIds];
      const from = order.indexOf(categoryId);
      if (from === -1) return;
      const to = direction === "up" ? from - 1 : from + 1;
      if (to < 0 || to >= order.length) return; // already at the edge
      [order[from], order[to]] = [order[to]!, order[from]!];
      setSectionOrder(order);
      setVersion((v) => v + 1);
    },
    []
  );

  // Track previous pages for post-undo activePageId validation
  const prevPagesRef = useRef(pages);
  useEffect(() => {
    // After undo/redo, validate activePageId still exists
    if (prevPagesRef.current !== pages) {
      prevPagesRef.current = pages;
      const exists = pages.some((p) => p.id === activePageId);
      if (!exists && pages.length > 0) {
        setActivePageIdRaw(pages[0]!.id);
      }
    }
  }, [pages, activePageId]);

  // ── Derived state ─────────────────────────────────────────────────

  const activePageWidgets = useMemo(() => {
    const page = pages.find((p) => p.id === activePageId);
    return page?.widgets ?? pages[0]?.widgets ?? [];
  }, [pages, activePageId]);

  // ── Page navigation ───────────────────────────────────────────────

  const setActivePageId = useCallback((id: string) => {
    setActivePageIdRaw(id);
  }, []);

  // ── Helper: normalize order values ────────────────────────────────

  const normalizeOrder = (arr: ReportPage[]): ReportPage[] =>
    arr.map((p, i) => (p.order === i ? p : { ...p, order: i }));

  // ── Helper: update pages with version bump ────────────────────────

  const setPagesWithVersion = useCallback(
    (next: ReportPage[] | ((prev: ReportPage[]) => ReportPage[])) => {
      setPages(next);
      setVersion((v) => v + 1);
    },
    [setPages]
  );

  const setPagesSilentWithVersion = useCallback(
    (next: ReportPage[] | ((prev: ReportPage[]) => ReportPage[])) => {
      setPagesSilent(next);
      setVersion((v) => v + 1);
    },
    [setPagesSilent]
  );

  // ── Page CRUD operations ──────────────────────────────────────────

  const addPage = useCallback(
    (category?: string) => {
      let newPageId = "";
      const validCategory =
        category && PAGE_CATEGORY_IDS.has(category) ? category : DEFAULT_CATEGORY;
      setPagesWithVersion((prev) => {
        if (prev.length >= MAX_PAGES) return prev;
        const newPage: ReportPage = {
          id: crypto.randomUUID(),
          title: `Page ${prev.length + 1}`,
          order: prev.length,
          category: validCategory,
          widgets: [],
        };
        newPageId = newPage.id;
        return [...prev, newPage];
      });
      if (newPageId) setActivePageIdRaw(newPageId);
    },
    [setPagesWithVersion]
  );

  const deletePage = useCallback(
    (id: string) => {
      let newActiveId = "";
      setPagesWithVersion((prev) => {
        if (prev.length <= 1) return prev; // Min 1 page guard
        const idx = prev.findIndex((p) => p.id === id);
        if (idx === -1) return prev;
        const next = normalizeOrder(prev.filter((p) => p.id !== id));
        // Update activePageId if deleted page was active (read from ref, not closure)
        if (activePageIdRef.current === id) {
          newActiveId = next[Math.max(0, idx - 1)]?.id ?? next[0]?.id ?? "";
        }
        return next;
      });
      if (newActiveId) setActivePageIdRaw(newActiveId);
    },
    [setPagesWithVersion]
  );

  const duplicatePage = useCallback(
    (id: string) => {
      let newPageId = "";
      setPagesWithVersion((prev) => {
        if (prev.length >= MAX_PAGES) return prev;
        const sourceIdx = prev.findIndex((p) => p.id === id);
        if (sourceIdx === -1) return prev;
        const source = prev[sourceIdx]!;

        // Check total widget count
        const totalWidgets =
          prev.reduce((sum, p) => sum + p.widgets.length, 0) +
          source.widgets.length;
        if (totalWidgets > MAX_WIDGETS_TOTAL) return prev;

        const newPage: ReportPage = {
          id: crypto.randomUUID(),
          title: `${source.title} (copy)`,
          order: source.order + 1,
          category: source.category ?? DEFAULT_CATEGORY,
          widgets: source.widgets.map((w) => ({
            ...w,
            id: crypto.randomUUID(),
          })),
        };

        // Insert after source, then normalize order
        const next = [...prev];
        next.splice(sourceIdx + 1, 0, newPage);
        newPageId = newPage.id;
        return normalizeOrder(next);
      });
      if (newPageId) setActivePageIdRaw(newPageId);
    },
    [setPagesWithVersion]
  );

  const reorderPages = useCallback(
    (fromIndex: number, toIndex: number) => {
      setPagesWithVersion((prev) => {
        if (fromIndex === toIndex) return prev;
        const clamped = {
          from: Math.max(0, Math.min(fromIndex, prev.length - 1)),
          to: Math.max(0, Math.min(toIndex, prev.length - 1)),
        };
        if (clamped.from === clamped.to) return prev;

        const next = [...prev];
        const [moved] = next.splice(clamped.from, 1);
        next.splice(clamped.to, 0, moved!);
        return normalizeOrder(next);
      });
    },
    [setPagesWithVersion]
  );

  const renamePage = useCallback(
    (id: string, title: string): boolean => {
      const trimmed = title.trim();
      if (trimmed.length === 0) return false;
      if (trimmed.length > MAX_PAGE_TITLE_LENGTH) return false;
      if (HTML_INJECTION_PATTERN.test(trimmed)) return false;

      setPagesWithVersion((prev) =>
        prev.map((p) => (p.id === id ? { ...p, title: trimmed } : p))
      );
      return true;
    },
    [setPagesWithVersion]
  );

  // ── Category operations ─────────────────────────────────────────────

  const changePageCategory = useCallback(
    (id: string, category: string) => {
      if (!PAGE_CATEGORY_IDS.has(category)) return;
      setPagesWithVersion((prev) =>
        prev.map((p) => (p.id === id ? { ...p, category } : p))
      );
    },
    [setPagesWithVersion]
  );

  // ── Widget operations scoped to active page ───────────────────────

  const updateActivePageWidgets = useCallback(
    (
      widgets:
        | ReportPageWidget[]
        | ((prev: ReportPageWidget[]) => ReportPageWidget[])
    ) => {
      // Capture activePageId at call time to avoid stale closure
      const pageId = activePageId;
      setPagesWithVersion((prev) => {
        const pageIdx = prev.findIndex((p) => p.id === pageId);
        if (pageIdx === -1) return prev;
        const page = prev[pageIdx]!;
        const nextWidgets =
          typeof widgets === "function" ? widgets(page.widgets) : widgets;
        const next = [...prev];
        next[pageIdx] = { ...page, widgets: nextWidgets };
        return next;
      });
    },
    [activePageId, setPagesWithVersion]
  );

  const updateActivePageWidgetsSilent = useCallback(
    (
      widgets:
        | ReportPageWidget[]
        | ((prev: ReportPageWidget[]) => ReportPageWidget[])
    ) => {
      const pageId = activePageId;
      setPagesSilentWithVersion((prev) => {
        const pageIdx = prev.findIndex((p) => p.id === pageId);
        if (pageIdx === -1) return prev;
        const page = prev[pageIdx]!;
        const nextWidgets =
          typeof widgets === "function" ? widgets(page.widgets) : widgets;
        const next = [...prev];
        next[pageIdx] = { ...page, widgets: nextWidgets };
        return next;
      });
    },
    [activePageId, setPagesSilentWithVersion]
  );

  // ── Undo/redo with activePageId validation ────────────────────────

  const undo = useCallback(() => {
    undoRaw();
    setVersion((v) => v + 1);
  }, [undoRaw]);

  const redo = useCallback(() => {
    redoRaw();
    setVersion((v) => v + 1);
  }, [redoRaw]);

  // ── Reset ─────────────────────────────────────────────────────────

  const reset = useCallback(
    (
      newPages: ReportPage[],
      newSectionLabels?: Record<string, string>,
      newActiveSections?: string[],
      newSectionOrder?: string[]
    ) => {
      resetUndoStack(newPages);
      setActivePageIdRaw(newPages[0]?.id ?? "");
      setSectionLabels(newSectionLabels ?? {});
      setActiveSections(
        (newActiveSections ?? []).filter((id) => PAGE_CATEGORY_IDS.has(id))
      );
      setSectionOrder(
        (newSectionOrder ?? []).filter((id) => PAGE_CATEGORY_IDS.has(id))
      );
      setVersion(0);
    },
    [resetUndoStack]
  );

  return {
    pages,
    activePageId,
    activePageWidgets,
    addPage,
    deletePage,
    duplicatePage,
    reorderPages,
    renamePage,
    changePageCategory,
    sectionLabels,
    setSectionLabel,
    activeSections,
    addSection,
    deleteSection,
    sectionOrder,
    moveSection,
    setActivePageId,
    updateActivePageWidgets,
    updateActivePageWidgetsSilent,
    undo,
    redo,
    canUndo,
    canRedo,
    reset,
    version,
  };
}
